import { z } from 'zod';
import { RunSettings } from './run-settings.js';

/** What a tool can do to the world. Unknown tools are classified as `write` (fail safe). */
export const TOOL_EFFECTS = ['read', 'write', 'external', 'money', 'destructive'] as const;
export const ToolEffect = z.enum(TOOL_EFFECTS);
export type ToolEffect = z.infer<typeof ToolEffect>;

export const POLICY_DECISIONS = ['allow', 'ask', 'deny'] as const;
export const PolicyDecision = z.enum(POLICY_DECISIONS);
export type PolicyDecision = z.infer<typeof PolicyDecision>;

/** Default decision per effect when no explicit rule matches. */
export const DEFAULT_EFFECT_DECISIONS: Record<ToolEffect, PolicyDecision> = {
  read: 'allow',
  write: 'ask',
  external: 'ask',
  money: 'ask',
  destructive: 'ask',
};

export const ToolPolicyRule = z.object({
  /** Glob over tool names, e.g. `mcp.github.*`, `sandbox_exec`, `*`. */
  toolPattern: z.string().min(1).max(200),
  /** Optionally restrict the rule to tools with this effect. */
  effect: ToolEffect.optional(),
  decision: PolicyDecision,
  priority: z.number().int().default(0),
});
export type ToolPolicyRule = z.infer<typeof ToolPolicyRule>;

/** Most entries the egress proxy takes per sandbox (the runner rejects longer allowlists). */
export const MAX_EGRESS_HOSTS = 64;
const HOST_LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;

/**
 * Allowlist entry for one host an agent needs (request_network_access, the run view's "allow" button):
 * an exact hostname with an optional port. A URL is reduced to its host (and a non-default port). Never an
 * IP literal, a wildcard or a single-label (intranet) name. Returns null when the input is not such a host.
 */
export function egressHostEntry(raw: string): string | null {
  let s = raw.trim().toLowerCase();
  // A URL: keep its authority (no `URL` here: this package runs in browsers and Node without DOM types).
  const url = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]*)/.exec(s);
  if (url) s = url[1]!;
  if (s.includes('@')) return null;
  let port = '';
  const m = /^(.*):(\d{1,5})$/.exec(s);
  if (m) {
    s = m[1]!;
    const n = Number(m[2]);
    if (n < 1 || n > 65535) return null;
    if (n !== 80 && n !== 443) port = `:${n}`;
  }
  if (s.endsWith('.')) s = s.slice(0, -1);
  if (s.length > 253) return null;
  const labels = s.split('.');
  if (labels.length < 2 || !labels.every((l) => HOST_LABEL.test(l))) return null;
  // A numeric last label is an IP literal (or not a hostname at all).
  if (/^\d+$/.test(labels.at(-1)!)) return null;
  return `${s}${port}`;
}

export const Guardrails = z.object({
  ...RunSettings.shape,
  /** Codex agents: native tools require all workspace tool policies to allow execution. */
  codex: z
    .object({
      sandbox: z.enum(['auto', 'off']).default('auto'),
      claudeFallbackModelRef: z.string().min(1).max(300).nullable().optional(),
      claudeFallbackReasoningEffort: z.enum(['none', 'low', 'medium', 'high']).nullable().optional(),
    })
    .default({ sandbox: 'auto' }),
  maxSteps: z.number().int().min(1).max(500).default(25),
  maxSubagentDepth: z.number().int().min(0).max(5).default(2),
  maxSubagents: z.number().int().min(0).max(20).default(5),
  maxRunCostUsd: z.number().min(0).max(1000).default(2),
  maxRunTokens: z.number().int().min(0).default(500_000),
  /**
   * Tasks the agent may queue for itself in one chain with `queue_next_tasks` (each runs in a fresh
   * session with its own step/token/cost limits). 0 = the agent cannot queue follow-up tasks.
   */
  maxQueuedTasks: z.number().int().min(0).max(200).default(20),
  /** Domains the agent's sandbox may reach (egress proxy allowlist). */
  egressAllowlist: z.array(z.string().max(253)).max(200).default([]),
  /**
   * A message (send_message_to_agent / chat) that arrives while the run is finishing is read with one
   * more continuation instead of expiring. Costs an extra model turn, so off by default (docs/run-inbox.md).
   */
  readLateMessages: z.boolean().default(false),
  /**
   * A DM or @mention while the agent is idle starts a short reply run (a "wake", docs/company-channels.md).
   * Costs a run and tokens per wake; loop guards cap it. Off: the message waits for its next run.
   */
  respondWhenMentioned: z.boolean().default(true),
  /** Agents this agent may delegate tasks to (ids). Empty = reports + manager. */
  delegateTo: z.array(z.string()).max(100).default([]),
  /** Rule-of-Two flags: the agent sees untrusted input / sensitive data / can act externally. */
  ruleOfTwo: z
    .object({
      untrustedInput: z.boolean().default(false),
      sensitiveData: z.boolean().default(false),
      externalActions: z.boolean().default(false),
      adminOverride: z.boolean().default(false),
    })
    .default({ untrustedInput: false, sensitiveData: false, externalActions: false, adminOverride: false }),
  /**
   * Admin-set sandbox limits. `memoryMb` is the agent's **maximum**: the sandbox starts at the runner
   * default and the platform doubles it after out-of-memory kills, up to this value (and never above the
   * runner's RUNNER_MAX_MEMORY_MB). `cpus` is fixed (CPU starvation cannot be detected reliably).
   */
  sandboxLimits: z
    .object({
      memoryMb: z.number().int().min(256).max(65_536).optional(),
      cpus: z.number().min(0.25).max(32).optional(),
    })
    .default({}),
  /** Git workspaces (docs/git-workspaces.md). */
  git: z
    .object({
      /**
       * `auto` (default): agents with a GitHub/GitLab connection that may write get a local checkout in
       * their sandbox (repo_* + file tools) instead of per-file MCP writes. `off`: never.
       */
      workspace: z.enum(['auto', 'off']).default('auto'),
      /**
       * Remote branches the agent may push to directly with `repo_push` (globs, e.g. `main`, `release/*`).
       * Empty (default) = pull/merge requests only.
       */
      pushBranches: z.array(z.string().min(1).max(200)).max(50).default([]),
      /**
       * Default on: when the agent pushed its work branch in a run, report_result reads that branch's CI
       * once (no model call); failing CI refuses the first report_result. `false` turns the check off.
       */
      checkCiOnReport: z.boolean().optional(),
    })
    .default({ workspace: 'auto', pushBranches: [] }),
  /** Claude Code agents (claude-agent-sdk runtime). */
  claudeCode: z
    .object({
      /**
       * `auto` (default): an agent with sandbox tools runs Claude Code inside its sandbox, with Claude Code's
       * own tools, prompt and context handling (policy and approvals unchanged). `off`: Claude Code runs in
       * the cli-runner and uses the platform's sandbox tools over MCP.
       */
      sandbox: z.enum(['auto', 'off']).default('auto'),
      /** OpenAI model override for this agent; omitted/null uses the company fallback default. */
      codexFallbackModelRef: z.string().min(1).max(300).nullable().optional(),
      codexFallbackReasoningEffort: z.enum(['none', 'low', 'medium', 'high']).nullable().optional(),
    })
    .default({ sandbox: 'auto' }),
  approvalTtlHours: z
    .number()
    .min(0.1)
    .max(24 * 14)
    .default(24),
});
export type Guardrails = z.infer<typeof Guardrails>;

/** Company-wide switches and defaults. Agent model/effort overrides take precedence. */
export const CompanyFallbackPolicy = z.object({
  claudeToCodex: z.boolean().default(true),
  codexToClaude: z.boolean().default(true),
  codexModelRef: z.string().min(1).max(300).nullable().default(null),
  codexReasoningEffort: z.enum(['none', 'low', 'medium', 'high']).nullable().default(null),
  claudeModelRef: z.string().min(1).max(300).nullable().default(null),
  claudeReasoningEffort: z.enum(['none', 'low', 'medium', 'high']).nullable().default(null),
});
export type CompanyFallbackPolicy = z.infer<typeof CompanyFallbackPolicy>;
export const CompanyFallbackPolicyPatch = z
  .object({
    claudeToCodex: z.boolean().optional(),
    codexToClaude: z.boolean().optional(),
    codexModelRef: z.string().min(1).max(300).nullable().optional(),
    codexReasoningEffort: z.enum(['none', 'low', 'medium', 'high']).nullable().optional(),
    claudeModelRef: z.string().min(1).max(300).nullable().optional(),
    claudeReasoningEffort: z.enum(['none', 'low', 'medium', 'high']).nullable().optional(),
  })
  .strict();
export type CompanyFallbackPolicyPatch = z.infer<typeof CompanyFallbackPolicyPatch>;

/** A narrow patch for fallback settings; omitted values preserve the agent's configuration. */
const FallbackTargetSettings = z
  .object({
    modelRef: z.string().min(1).max(300).nullable().optional(),
    reasoningEffort: z.enum(['none', 'low', 'medium', 'high']).nullable().optional(),
  })
  .strict();
export const SubscriptionFallbackSettings = z.object({
  codex: FallbackTargetSettings.optional(),
  claude: FallbackTargetSettings.optional(),
});
export type SubscriptionFallbackSettings = z.infer<typeof SubscriptionFallbackSettings>;

export function applySubscriptionFallbackSettings(
  g: Guardrails,
  patch: SubscriptionFallbackSettings,
): Guardrails {
  const c = patch.codex;
  const a = patch.claude;
  return {
    ...g,
    claudeCode: {
      ...g.claudeCode,
      ...(c?.modelRef !== undefined ? { codexFallbackModelRef: c.modelRef } : {}),
      ...(c?.reasoningEffort !== undefined ? { codexFallbackReasoningEffort: c.reasoningEffort } : {}),
    },
    codex: {
      ...g.codex,
      ...(a?.modelRef !== undefined ? { claudeFallbackModelRef: a.modelRef } : {}),
      ...(a?.reasoningEffort !== undefined ? { claudeFallbackReasoningEffort: a.reasoningEffort } : {}),
    },
  };
}

export const ModelParams = z.object({
  temperature: z.number().min(0).max(2).optional(),
  maxOutputTokens: z.number().int().min(1).max(200_000).optional(),
  reasoningEffort: z.enum(['none', 'low', 'medium', 'high']).optional(),
});
export type ModelParams = z.infer<typeof ModelParams>;

/**
 * Agent runtime (per agent version):
 * - `ai-sdk`: the Vercel AI SDK tool loop in the worker — any provider.
 * - `claude-agent-sdk`: the Claude Agent SDK (bundled Claude Code CLI subprocess) — Anthropic
 *   providers only, authenticated with the org's Anthropic API key; built-in Claude Code tools are
 *   disabled, every tool is a platform/sandbox/MCP tool behind the same policy.
 * - `codex-sdk`: the OpenAI Codex SDK — OpenAI providers only (runtime lives in agent-core/codex-sdk).
 */
export const AGENT_RUNTIMES = ['ai-sdk', 'claude-agent-sdk', 'codex-sdk'] as const;

/** Provider type a runtime is restricted to (absent = any provider). */
export const RUNTIME_PROVIDER: Partial<Record<(typeof AGENT_RUNTIMES)[number], 'anthropic' | 'openai'>> = {
  'claude-agent-sdk': 'anthropic',
  'codex-sdk': 'openai',
};
export const AgentRuntimeId = z.enum(AGENT_RUNTIMES);
export type AgentRuntimeId = z.infer<typeof AgentRuntimeId>;

/** Rule of Two: an agent may have at most two of (untrusted input, sensitive data, external actions). */
export function violatesRuleOfTwo(g: Guardrails): boolean {
  const r = g.ruleOfTwo;
  const count = Number(r.untrustedInput) + Number(r.sensitiveData) + Number(r.externalActions);
  return count === 3 && !r.adminOverride;
}
